/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, Injector } from '@angular/core';
import { HttpDataService } from './http-data.service';
import { CsvService } from './csv.service';
import { StoreService } from './store.service';
import { Observable, BehaviorSubject, lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { ProfileService } from './profile.service';
import { CoursesService } from './courses.service';
import { Router } from '@angular/router';

const PENDING_JOBS = 'PENDING_JOBS'; // key to save on localstorage pending jobs
const POLLING_TIME = 3000; // time interval between job status requests
const INACTIVE_MARGIN = 300; // time to consider a job as inactive and cancel polling
const DEAD_MARGIN = 300; // time to consider a job as dead and delete from cache
@Injectable()
export class JobsService extends HttpDataService {
  public jobs = new Map<string, any>();
  public jobsSubject = new BehaviorSubject(this.jobs);

  public jobTypes: any = {
    CREATE_USERS_BATCH: 'CREATE_USERS_BATCH',
    CREATE_QUESTIONS_BULK: 'CREATE_QUESTIONS_BULK',
    CHANGE_USER_EMAIL_BATCH: 'CHANGE_USER_EMAIL_BATCH',
    CREATE_LICENSES_BATCH: 'CREATE_LICENSES_BATCH',
    UPDATE_USERS_BULK: 'UPDATE_USERS_BULK',
    ACTIVATE_USERS_BULK: 'ACTIVATE_USERS_BULK',
    DEACTIVATE_USERS_BULK: 'DEACTIVATE_USERS_BULK',
    DELETE_USERS_BULK: 'DELETE_USERS_BULK',
    ANONYMIZE_USERS_BULK: 'ANONYMIZE_USERS_BULK',
    UPDATE_TAGS_BULK: 'UPDATE_TAGS_BULK',
    CANCEL_LICENSES_BULK: 'CANCEL_LICENSES_BULK',
    SEND_START_CLASS_LICENSES_BULK: 'SEND_START_CLASS_LICENSES_BULK',
    EXTEND_LICENSES_BULK: 'EXTEND_LICENSES_BULK',
    CHANGE_CLASS_LICENSES_BULK: 'CHANGE_CLASS_LICENSES_BULK',
    UPDATE_COURSE_VISIBILITY_BULK: 'UPDATE_COURSE_VISIBILITY_BULK',
    DOWNLOAD_CLASS_DIPLOMAS_BULK: 'DOWNLOAD_CLASS_DIPLOMAS_BULK',
    SEND_PERSONALIZED_EMAIL_BULK: 'SEND_PERSONALIZED_EMAIL_BULK',
    SEND_WELCOME_EMAIL_BULK: 'SEND_WELCOME_EMAIL_BULK',
    DOWNLOAD_LICENSES_REPORT: 'DOWNLOAD_LICENSES_REPORT',
    DOWNLOAD_REPORT: 'DOWNLOAD_REPORT',
    DOWNLOAD_COURSE_DOCUMENTATION_BULK: 'DOWNLOAD_COURSE_DOCUMENTATION_BULK',
    DOWNLOAD_COURSE_SCORM_BULK: 'DOWNLOAD_COURSE_SCORM_BULK',
    SEND_K1_EMAIL_BULK: 'SEND_K1_EMAIL_BULK',
    SEND_M1_EMAIL_BULK: 'SEND_M1_EMAIL_BULK',
    UPDATE_ITINERARIES_DIPLOMA_BULK: 'UPDATE_ITINERARIES_DIPLOMA_BULK',
    UPDATE_COURSE_CONFIG_BULK: 'UPDATE_COURSE_CONFIG_BULK',
    CREATE_PLAYERS_BULK: 'CREATE_PLAYERS_BULK',
    DEACTIVATE_PLAYERS_BULK: 'DEACTIVATE_PLAYERS_BULK',
    ANONYMIZE_PLAYERS_BULK: 'ANONYMIZE_PLAYERS_BULK',
    UNSUBSCRIBE_PLAYERS_BULK: 'UNSUBSCRIBE_PLAYERS_BULK',
    DOWNLOAD_DOCUMENTATION_COURSES: 'DOWNLOAD_DOCUMENTATION_COURSES',
    DOWNLOAD_MATCH_RANKING: 'DOWNLOAD_MATCH_RANKING',
    DOWNLOAD_GROUP_RANKING: 'DOWNLOAD_GROUP_RANKING',
    DOWNLOAD_FEDERATIONS_RANKING: 'DOWNLOAD_FEDERATIONS_RANKING',
    UPLOAD_KPI_TEMPLATES: 'UPLOAD_KPI_TEMPLATES'
  };

  public fileJobsResult = [
    this.jobTypes.DOWNLOAD_CLASS_DIPLOMAS_BULK,
    this.jobTypes.DOWNLOAD_LICENSES_REPORT,
    this.jobTypes.DOWNLOAD_REPORT,
    this.jobTypes.DOWNLOAD_COURSE_DOCUMENTATION_BULK,
    this.jobTypes.DOWNLOAD_COURSE_SCORM_BULK
  ];

  public navJobResult = [
    this.jobTypes.CREATE_USERS_BATCH,
    this.jobTypes.CREATE_QUESTIONS_BULK,
    this.jobTypes.CREATE_LICENSES_BATCH,
    this.jobTypes.CREATE_PLAYERS_BULK,
    this.jobTypes.UPLOAD_KPI_TEMPLATES
  ];

  constructor(
    protected injector: Injector,
    private csvService: CsvService,
    private profileService: ProfileService,
    private coursesService: CoursesService,
    private router: Router
  ) {
    super(injector, '/jobs');
    this.profileService.getLoggedUserRole$().subscribe((user) => {
      if (user) {
        this.loadJobsCache();
      }
    });
  }

  async saveJobsCache() {
    const jobsCache = {};
    for (const jobId of this.jobs.keys()) {
      const value = await lastValueFrom(this.jobs.get(jobId).pipe(take(1)));
      jobsCache[jobId] = value['type'];
    }
    localStorage.setItem(PENDING_JOBS, JSON.stringify(jobsCache));
  }

  loadJobsCache() {
    const storedJobs = localStorage.getItem(PENDING_JOBS);
    if (storedJobs) {
      const jobsCache = JSON.parse(storedJobs);
      Object.keys(jobsCache).forEach((jobId) => this.manageJob(jobId, jobsCache[jobId]));
    }
  }

  public getJobs$(): Observable<any> {
    return this.jobsSubject.asObservable();
  }

  public getJobInfo$(jobId) {
    return this.jobs.get(jobId);
  }

  getJobInfo(jobId): Promise<any> {
    return lastValueFrom(this.http.get(`${this.baseUrl}/reports/${jobId}`)).then((response) => response);
  }

  manageJob(jobId?: string, jobType?: string): Observable<any> {
    const jobObservable = StoreService.create(jobId, { type: jobType });
    this.jobs.set(jobId, jobObservable);
    this.jobsSubject.next(this.jobs);
    this.saveJobsCache();
    this.getJobStatus$(jobId);
    return jobObservable;
  }

  async getJobStatus$(jobId) {
    const interval = setInterval(async () => {
      const jobInfo = await this.getJobInfo(jobId);
      jobInfo.inactiveTime = Math.round((Date.now() - new Date(jobInfo.updated_at).getTime()) / 1000);
      if (jobInfo.status === 'finished' || jobInfo.status === 'fatal-error' || jobInfo.inactiveTime > INACTIVE_MARGIN) {
        clearInterval(interval);
      }
      if (jobInfo.inactiveTime > DEAD_MARGIN) {
        this.deleteJob(jobId);
      } else {
        StoreService.merge(jobId, jobInfo);
      }
    }, POLLING_TIME);
  }

  deleteJob(jobId) {
    StoreService.remove(jobId);
    this.jobs.delete(jobId);
    this.jobsSubject.next(this.jobs);
    this.saveJobsCache();
  }

  getStudentFormat(item: any) {
    const formatted = {
      name: item.name,
      surname: item.surname,
      email: item.email,
      phone: item.phone,
      pid: item.pid,
      comments: item.comments,
      lang: item.lang,
      alias: item.alias,
      tag1: item.tag1,
      tag2: item.tag2,
      tag3: item.tag3,
      tag4: item.tag4,
      tag5: item.tag5
    };
    if (item.error) formatted['error'] = item.error;
    if (item.latestCompletedLicenseCreatedAt)
      formatted['latestCompletedLicenseCreatedAt'] = item.latestCompletedLicenseCreatedAt;
    return formatted;
  }

  getCourseFormat(item: any) {
    const { courseId, error, lang } = item;
    const course = this.coursesService.getCachedCourseData(courseId);
    const data = {
      name: course ? course.name : courseId,
      error
    };
    if (lang) data['lang'] = lang;
    return data;
  }

  formatCsv(item: any, type: string) {
    let info;
    switch (type) {
      case this.jobTypes.CREATE_LICENSES_BATCH:
      case this.jobTypes.CREATE_USERS_BATCH:
        info = this.getStudentFormat(item);
        break;
      case this.jobTypes.UPDATE_COURSE_CONFIG_BULK:
      case this.jobTypes.DOWNLOAD_COURSE_DOCUMENTATION_BULK:
        info = this.getCourseFormat(item);
        break;
      default:
        info = {
          email: item.email,
          error: item.error
        };
        break;
    }
    return info;
  }

  downloadCSV(list: any[], type: string) {
    const formattedCsv = list.map((item) => this.formatCsv(item, type));
    const headers = Object.keys(formattedCsv[0]);
    this.csvService.dataToCsv(formattedCsv, type.toLowerCase(), { headers, shouldTranslateHeaders: true });
  }

  /**
   * Navigate to the url from the job
   * @param url
   * @param type
   */
  public navUrlLink(url: string, type?: string): void {
    const tempUrl: string[] = url.split('#');
    this.router.navigate([tempUrl[0]], { fragment: tempUrl[1] }).then();
  }
}
